// =============================================================================
// src/features/analytics/analytics-insights-block-a-data.ts - Bloco A,
// "Estrutura da disputa" (cards 01-03). Proveniencia: Diario_Anexo-
// CardsTaticos_2026-07-25.md linhas 44-176, portado verbatim em 2026-07-27.
// =============================================================================

import type { AnalyticsInsightBlock, AnalyticsInsightCard } from './analytics-insights-types';

const ANALYTICS_INSIGHT_CARD_01: AnalyticsInsightCard = {
  cardNumber: 1,
  cardTitle: 'A base de Roberto Cidade e a de Wilson Lima são a mesma — e o endosso é público',
  priorityTitle: 'A base de dois adversários é a mesma, com prova pública',
  impactScore: 4,
  classificationLabel: 'AMEAÇA ESTRUTURAL',
  evidenceBlocks: [
    {
      blockKind: 'paragraph',
      paragraphHtml:
        'Cruzando a identidade de todos os comentaristas dos oito dias, <strong>80 pessoas comentam tanto no perfil ' +
        'de Roberto Cidade quanto no de Wilson Lima</strong>. É o maior par da praça, e os três maiores pares entre ' +
        'campos tidos como adversários são todos internos ao mesmo bloco.',
    },
    {
      blockKind: 'table',
      columnHeaders: ['Par de campos', 'Comentaristas em comum', 'Como proporção da base menor'],
      tableRows: [
        ['<strong>Cidade e Wilson Lima</strong>', '<strong>80</strong>', '11,6%'],
        ['Serafim Corrêa e Wilson Lima', '14', '<strong>17,5%</strong>'],
        ['Cidade e Serafim Corrêa', '11', '13,8%'],
        ['Monitorado e Eduardo Braga (chapa)', '18', '7,9%'],
        ['Cidade e David Almeida', '6', '0,9%'],
        ['Monitorado e Cidade / Wilson / David / Maria', '<strong>0</strong>', '—'],
      ],
    },
    {
      blockKind: 'paragraph',
      paragraphHtml:
        'A sobreposição está distribuída em <strong>49 posts distintos e 392 pares autor-post</strong> — não é efeito de ' +
        'uma publicação viral. E há uma segunda evidência, de natureza inteiramente diferente da primeira: a ' +
        'publicação de melhor desempenho de Wilson Lima em todo o período é um elogio nominal ao adversário ' +
        'aparente, com 2.731 de engajamento.',
    },
    {
      blockKind: 'quote',
      quoteHtml:
        '"Fico feliz em ver o governador Roberto Cidade avançando com o Auxílio Estadual com compromisso e ' +
        'sensibilidade. Esse é um programa que transforma a vida de milhares de famílias amazonenses."',
      quoteAttribution: 'publicação de Wilson Lima, a de maior engajamento dele na janela',
    },
  ],
  tacticalReadingHtml: [
    'Dois métodos independentes — identidade de audiência e endosso nominal público — apontam para o ' +
      'mesmo bloco. Quando concordam, a leitura deixa de ser inferência estatística e passa a ser fato ' +
      'declarado pelo próprio ator. A consequência que o número não entrega: esse bloco não precisa ser ' +
      'denunciado, porque ele já se declarou. Tratar Cidade e Wilson Lima como dois adversários separados, ' +
      'com duas estratégias separadas, é gastar duas vezes contra o mesmo alvo. A linha zero da última ' +
      'coluna diz o resto — não há uma única pessoa que circule entre a base própria e a de qualquer um dos ' +
      'quatro adversários, o que aparece no CARD 02 como propriedade da base própria.',
  ],
  actionCallHtml:
    'Consolidar Cidade e Wilson Lima como um bloco único no planejamento de contraste e no mapa de ' +
    'alianças, com uma peça só, e não duas. O endosso público é material utilizável a qualquer momento; ' +
    'perde valor à medida que o ciclo avança e o gesto envelhece.',
  confidenceLine:
    'ALTA — validado contra artefato de post único (49 posts distintos, 392 pares autor-post) e contra artefato de plataforma',
  natureLine: 'estrutural',
  baseLine: '3.148 comentaristas em perfis políticos, 17 a 24/07',
  validityLine: 'revalidar na próxima edição · ação até 08/08/2026',
  seriesLine: '2ª edição · aberto desde 25/07 · deriva instrumental, tese e impacto estáveis',
};

const ANALYTICS_INSIGHT_CARD_02: AnalyticsInsightCard = {
  cardNumber: 2,
  cardTitle: 'A base própria é a menor da disputa e a única sem porta de entrada',
  priorityTitle: 'A base própria é a menor da disputa e a única sem porta de entrada',
  impactScore: 5,
  classificationLabel: 'AMEAÇA ESTRUTURAL',
  evidenceBlocks: [
    {
      blockKind: 'paragraph',
      paragraphHtml:
        'Contando pessoas distintas — não registros —, a base de comentaristas do monitorado é a menor entre ' +
        'os campos com presença relevante na praça, e a maior da disputa é 3,7 vezes maior.',
    },
    {
      blockKind: 'table',
      columnHeaders: ['Campo', 'Comentaristas distintos', 'Também comentam em outro campo'],
      tableRows: [
        ['David Almeida', '<strong>833</strong>', '1,4%'],
        ['Roberto Cidade', '702', '13,5%'],
        ['Wilson Lima', '689', '13,9%'],
        ['Maria do Carmo', '399', '2,0%'],
        ['Eduardo Braga (chapa)', '357', '6,2%'],
        ['<strong>Monitorado</strong>', '<strong>227</strong>', '<strong>7,9%</strong>'],
        ['Serafim Corrêa', '80', '23,8%'],
      ],
    },
    {
      blockKind: 'paragraph',
      paragraphHtml:
        'E a praça inteira é fechada: dos 3.148 comentaristas de perfis políticos, <strong>95,8% comentam em um ' +
        'único campo</strong>. Apenas 123 pessoas circulam por dois campos e 8 por três.',
    },
  ],
  tacticalReadingHtml: [
    'Duas leituras se somam, e a segunda é a que importa. A primeira é o tamanho: a base própria é a ' +
      'menor. A segunda é a porosidade — em uma praça onde quase ninguém troca de campo, crescer por ' +
      'conversão de audiência adversária não é caminho disponível para ninguém, e o CARD 01 mostra que a ' +
      'única fronteira aberta do monitorado é com a própria chapa. Não há porta de entrada: o público que ' +
      'comenta em Omar Aziz não vem de outro campo político, e não existe fluxo espontâneo para trazê-lo. ' +
      'Isso desloca todo o crescimento possível para fora da praça partidária — que é exatamente onde o ' +
      'CARD 03 encontra 1.384 pessoas.',
  ],
  actionCallHtml:
    'Parar de disputar audiência adversária: os dados dizem que ela não se move. Redirecionar o esforço ' +
    'de crescimento para o público não-alinhado do CARD 03, que é 6,1 vezes maior que a base própria e ' +
    'não tem dono. Decisão de alocação, não de conteúdo — e ela precisa ser tomada nesta semana, porque ' +
    'o CARD 03 mostra o espaço ainda vazio.',
  confidenceLine:
    'ALTA — validado contra artefato de amostra desigual: o teto de coleta por publicação captura 7,2% dos comentários ' +
    'do monitorado contra 6,5% dos de David Almeida, ou seja, a captura favorece o monitorado e ainda assim a base dele é a menor',
  natureLine: 'estrutural',
  baseLine: '3.148 comentaristas distintos em perfis políticos, 17 a 24/07',
  validityLine: 'revalidar na próxima edição · ação até 01/08/2026',
  seriesLine: '2ª edição · aberto desde 25/07 · deriva instrumental, tese e impacto estáveis',
};

const ANALYTICS_INSIGHT_CARD_03: AnalyticsInsightCard = {
  cardNumber: 3,
  cardTitle: 'Há 1.384 pessoas sem lado escolhido, e as páginas onde elas estão não citam candidato',
  priorityTitle: '1.384 pessoas sem lado escolhido, em páginas que não citam candidato',
  impactScore: 5,
  classificationLabel: 'OPORTUNIDADE',
  evidenceBlocks: [
    {
      blockKind: 'paragraph',
      paragraphHtml:
        'Separando quem comenta em perfis políticos de quem comenta em páginas de imprensa, trânsito, bairro ' +
        'e humor, <strong>1.384 pessoas aparecem só do lado não-político</strong> — não comentaram em nenhum perfil de ' +
        'nenhum campo na janela. Contra 161 que circulam nos dois lados.',
    },
    {
      blockKind: 'table',
      columnHeaders: ['Página', 'Comentaristas', 'Já alinhados', 'Alcance', 'Publicações que citam candidato'],
      tableRows: [
        ['<strong>transitomanaus</strong>', '127', '<strong>0,8%</strong>', '<strong>579.677</strong>', '<strong>0</strong>'],
        ['<strong>manausnadepressao</strong>', '200', '<strong>1,5%</strong>', '<strong>458.653</strong>', '<strong>0</strong>'],
        ['portalacritica', '207', '0,0%', '61.803', '3'],
        ['portaldoholanda', '232', '5,2%', '259.356', '4'],
        ['bncamazonas', '249', '12,9%', '67.720', '13'],
      ],
    },
    {
      blockKind: 'paragraph',
      paragraphHtml:
        'As duas páginas de maior alcance da praça somam 1.038.330 visualizações na janela e <strong>nenhuma ' +
        'publicação citando qualquer candidato</strong>.',
    },
  ],
  tacticalReadingHtml: [
    'A coluna decisiva é a última, e ela inverte a leitura óbvia. Alcance alto com zero citação de ' +
      'candidato não significa "espaço tomado pelo adversário" — significa <strong>espaço vazio para todos</strong>. As ' +
      'duas maiores audiências não-partidárias da praça não foram ocupadas por ninguém, nem pelo monitorado ' +
      'nem pelos quatro adversários. Somado ao CARD 02, o quadro fecha: a praça partidária é fechada e não ' +
      'troca de gente, e o único público disponível está em páginas que ainda não fizeram política. Quem ' +
      'entrar primeiro entra sem contraditório — e entra num ambiente onde política ainda não é assunto, ' +
      'o que é vantagem e restrição ao mesmo tempo.',
  ],
  actionCallHtml:
    'Construir presença nessas duas páginas por pauta de cidade — trânsito, custo de vida, permanência ' +
    'no estado —, não por pauta de candidatura. Entrar com discurso eleitoral num espaço que nunca citou ' +
    'candidato queima o espaço. Prazo curto: a janela vale enquanto a coluna de citação estiver em zero.',
  confidenceLine:
    'ALTA — validado contra a hipótese oposta (espaço já ocupado por adversário), refutada pela coluna de citação de candidato',
  natureLine: 'conjuntural — fecha quando o primeiro campo ocupar',
  baseLine: '1.545 comentaristas em páginas não-políticas, 17 a 24/07',
  validityLine: 'revalidar na próxima edição · ação até 01/08/2026',
  seriesLine:
    '2ª edição · aberto desde 25/07 · deriva instrumental (990 para 1.384 por completude de medição, não por crescimento do público)',
};

export const ANALYTICS_INSIGHTS_BLOCK_A: AnalyticsInsightBlock = {
  blockId: 'A',
  blockTitle: 'Bloco A — Estrutura da disputa',
  blockCards: [ANALYTICS_INSIGHT_CARD_01, ANALYTICS_INSIGHT_CARD_02, ANALYTICS_INSIGHT_CARD_03],
};
